import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	acceptCorrection,
	addCorrection,
	assistantCheck,
	assistantHint,
	assistantLookAgain,
	getAssistantPrice,
	getCorrections,
	retryCorrection,
	undoCorrection,
} from '@utils/api/videoProjects';
import { getUserFriendlyError } from '@utils/api/errorMessages';
import { useToast } from '@contexts/Toast.context';

/**
 * The corrections layer of one analysis (job), with add and undo. Changes show at once: an edit goes into the cached
 * layer before the server answers (with a temporary id) and is replaced by the stored one, or taken out if refused.
 */
const useCorrections = (projectUuid, jobUuid) => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	const key = useMemo(() => ['videoCorrections', projectUuid, jobUuid], [projectUuid, jobUuid]);
	// a change the server refused is taken back, and said (it used to vanish without a word)
	const refused = useCallback(
		(title, err) =>
			addToast({ id: 'video-correction-error', bg: 'danger', title, message: getUserFriendlyError(err) }),
		[addToast]
	);
	// an optimistic change must not be overwritten by a poll already on its way (the layer is polled every 3 s
	// while objects are followed); the server's layer after a change replaces the cache
	const holdPolls = useCallback(() => queryClient.cancelQueries({ queryKey: key }), [queryClient, key]);
	const takeLayer = useCallback(
		res =>
			res?.data &&
			queryClient.setQueryData(key, old => {
				// edits still on their way to the server stay shown
				const pending = (old?.data?.edits || []).filter(e => e.pending);
				return { ...res, data: { ...res.data, edits: [...(res.data.edits || []), ...pending] } };
			}),
		[queryClient, key]
	);
	const { data } = useQuery({
		queryKey: ['videoCorrections', projectUuid, jobUuid],
		queryFn: () => getCorrections(projectUuid, jobUuid),
		enabled: !!projectUuid && !!jobUuid,
		select: res => res?.data || null,
		// while a whole-video check runs its proposals arrive frame by frame, and an added object's track arrives
		// once it has been followed
		refetchInterval: q => {
			const layer = q.state.data?.data;
			const waiting =
				layer?.check?.status === 'running' ||
				(layer?.edits || []).some(e => e.status === 'following');
			return waiting ? 3000 : false;
		},
	});
	const setEdits = useCallback(
		change =>
			queryClient.setQueryData(key, old => {
				const layer = old?.data || { edits: [], revision: 0 };
				return { ...old, data: { ...layer, edits: change(layer.edits || []) } };
			}),
		[queryClient, key]
	);

	const { mutate: add } = useMutation({
		mutationFn: edit => addCorrection(projectUuid, jobUuid, edit),
		onMutate: async edit => {
			await holdPolls();
			const tempId = `pending-${Date.now()}`;
			// an object added by a click or a box is followed through the video on the server first
			const status = edit.type === 'add' ? 'following' : 'ready';
			setEdits(edits => [...edits, { ...edit, id: tempId, status, pending: true }]);
			return { tempId };
		},
		onSuccess: (res, _edit, ctx) =>
			setEdits(edits => edits.map(e => (e.id === ctx.tempId ? res.data : e))),
		onError: (err, _edit, ctx) => {
			setEdits(edits => edits.filter(e => e.id !== ctx?.tempId));
			refused('Could not add that', err);
		},
	});

	const { mutate: undo } = useMutation({
		mutationFn: editId => undoCorrection(projectUuid, jobUuid, editId),
		onMutate: async editId => {
			await holdPolls();
			let removed = null;
			let at = -1;
			setEdits(edits => {
				at = edits.findIndex(e => e.id === editId);
				removed = edits[at] || null;
				return edits.filter(e => e.id !== editId);
			});
			return { removed, at };
		},
		onSuccess: takeLayer,
		// back where it was in the list, not at its end
		onError: (err, _id, ctx) => {
			if (ctx?.removed)
				setEdits(edits => [...edits.slice(0, ctx.at), ctx.removed, ...edits.slice(ctx.at)]);
			refused('Could not undo that', err);
		},
	});

	// the assistant: its proposals land in the layer as `proposed` edits; accept makes one count, undo dismisses it
	const { data: price, refetch: refreshPrice } = useQuery({
		queryKey: ['videoAssistantPrice', projectUuid, jobUuid],
		queryFn: () => getAssistantPrice(projectUuid, jobUuid),
		enabled: !!projectUuid && !!jobUuid,
		select: res => res?.data || null,
		retry: false,
	});
	// the assistant's answer (AssistantResult: proposals, kept, rejected, credits) is what the call resolves to
	const afterAssistant = result => {
		setEdits(edits => [...edits, ...(result?.proposals || [])]);
		refreshPrice();
	};
	const { mutateAsync: lookAgain, isPending: lookingAgain } = useMutation({
		mutationFn: async frame => (await assistantLookAgain(projectUuid, jobUuid, frame))?.data,
		onSuccess: afterAssistant,
	});
	const { mutateAsync: hint, isPending: hinting } = useMutation({
		mutationFn: async ({ frame, text }) =>
			(await assistantHint(projectUuid, jobUuid, frame, text))?.data,
		onSuccess: afterAssistant,
	});
	const { mutate: accept } = useMutation({
		mutationFn: editId => acceptCorrection(projectUuid, jobUuid, editId),
		onMutate: async editId => {
			await holdPolls();
			setEdits(edits => edits.map(e => (e.id === editId ? { ...e, status: 'ready' } : e)));
		},
		// an accepted missed object comes back `following`: the server's layer says so
		onSuccess: takeLayer,
		onError: (err, editId) => {
			setEdits(edits => edits.map(e => (e.id === editId ? { ...e, status: 'proposed' } : e)));
			refused('Could not accept that', err);
		},
	});

	// an added object that could not be followed is tried again
	const { mutate: retry } = useMutation({
		mutationFn: editId => retryCorrection(projectUuid, jobUuid, editId),
		onMutate: async editId => {
			await holdPolls();
			setEdits(edits => edits.map(e => (e.id === editId ? { ...e, status: 'following' } : e)));
		},
		onSuccess: takeLayer,
		onError: (err, editId) => {
			setEdits(edits => edits.map(e => (e.id === editId ? { ...e, status: 'failed' } : e)));
			refused('Could not follow it again', err);
		},
	});

	const { mutateAsync: planCheck } = useMutation({
		mutationFn: () => assistantCheck(projectUuid, jobUuid, true),
	});
	const { mutateAsync: startCheck } = useMutation({
		mutationFn: () => assistantCheck(projectUuid, jobUuid, false),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
	});

	return {
		layer: data,
		planCheck,
		startCheck,
		add,
		undo,
		price,
		lookAgain,
		hint,
		accept,
		retry,
		assistantBusy: lookingAgain || hinting,
	};
};

export default useCorrections;
